"use client"

import { CategorizedResources } from "@/types/resources"
import { useMemo } from "react"
import { CategoryPanel } from "./categoryPanel"
import Pagination from "@/components/ui/pagination"
import { ResourceType } from "@/app/generated/prisma"

export function CategorizedDisplayContainer({
   resourceType,
   data,
   category,
   currentPage = 1,
}: {
   resourceType: ResourceType
   data?: CategorizedResources[]
   category: string
   currentPage?: number
}) {
   const categories = useMemo(
      () =>
         data?.map((group) => ({
            category_name: group.category_name,
            category_id: group.category_id,
         })) ?? [],
      [data],
   )

   const isAll = !category || category.toLowerCase() === "all"

   const activeIndex = useMemo(() => {
      if (isAll) return -1
      const index = categories.findIndex((cat) => cat.category_name.toLowerCase() === category.toLowerCase())
      return index >= 0 ? index : 0
   }, [categories, category, isAll])

   const allItems = useMemo(() => {
      if (!data) return []
      return data.flatMap((group) =>
         group.items.map((item) => ({
            ...item,
            description: item.description ?? "",
         })),
      )
   }, [data])

   const totalItemsForCategory = useMemo(() => {
      return isAll
         ? allItems
         : (data?.[activeIndex]?.items.map((item) => ({
              ...item,
              description: item.description ?? "",
           })) ?? [])
   }, [isAll, allItems, data, activeIndex])

   const limit = 10
   const totalPages = Math.ceil(totalItemsForCategory.length / limit)
   const offset = (currentPage - 1) * limit

   const paginatedItems = useMemo(() => {
      return totalItemsForCategory.slice(offset, offset + limit)
   }, [totalItemsForCategory, offset, limit])

   return (
      <div className="col-span-full w-full bg-white">
         <section aria-labelledby="features-heading">
            <div className="mx-auto px-4">
               <div className="mt-4">
                  {/* Panels */}
                  <section>
                     <CategoryPanel items={paginatedItems} type={resourceType} />
                  </section>

                  {totalPages > 1 && (
                     <div className="flex justify-center pt-8">
                        <Pagination currentPage={currentPage} totalPages={totalPages} baseUrl="" />
                     </div>
                  )}
               </div>
            </div>
         </section>
      </div>
   )
}
